import { useMutation, useQueryClient } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import {
  type MutationContext,
  libraryCatalogEntriesQueryKeys,
} from '@/hooks/library/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { EntityCatalogEntry } from '@/types/library/Entity'

interface UseUpdateCatalogEntryArgs {
  orgId: UUID | ''
  invalidateOnSuccess?: boolean
  optimisticUpdate?: boolean
  updateOnSuccess?: boolean
}

export const updateCatalogEntry = async (
  catalogEntry: EntityCatalogEntry,
  accessToken: string | undefined | null,
): Promise<void> => {
  if (accessToken === null || accessToken === undefined) {
    throw new Error(`No access token provided.`)
  }
  const body = {
    name: catalogEntry.name,
    description: catalogEntry.description,
    extended_data: catalogEntry.extended_data,
    archived: catalogEntry.archived,
    entity_type: catalogEntry.entity_type,
  }
  const res: Response = await fetch(
    `${API_URL}/api/v1/organizations/${catalogEntry.organization_id}/entities/${catalogEntry.id}`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: res,
    })
  }
}

export const useUpdateCatalogEntry = ({
  orgId,
  invalidateOnSuccess = true,
  optimisticUpdate = true,
  updateOnSuccess = true,
}: UseUpdateCatalogEntryArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const { handleError } = useHandleError()

  const queryClient = useQueryClient()

  return useMutation<
    void,
    Error,
    EntityCatalogEntry,
    MutationContext<EntityCatalogEntry>
  >({
    mutationFn: (catalogEntry) => updateCatalogEntry(catalogEntry, accessToken),
    onMutate: async (catalogEntry) => {
      const key = libraryCatalogEntriesQueryKeys.organization({ orgId })
      await queryClient.cancelQueries({ queryKey: key })

      // snapshot the previous value
      const previousData =
        queryClient.getQueryData<PaginatedResponse<EntityCatalogEntry>>(key)

      const updatedCatalogEntry = {
        ...catalogEntry,
        last_modified: new Date().toISOString(),
      }

      // optimistically update
      if (previousData && optimisticUpdate) {
        const updatedData = previousData.data.map((c) =>
          c.id === catalogEntry.id ? updatedCatalogEntry : c,
        )
        queryClient.setQueryData<PaginatedResponse<EntityCatalogEntry>>(key, {
          ...previousData,
          data: updatedData,
        })
      }

      const individualKey = libraryCatalogEntriesQueryKeys.entry({
        orgId,
        catalogEntryId: catalogEntry.id,
      })
      await queryClient.cancelQueries({ queryKey: individualKey })

      const individualData =
        queryClient.getQueryData<EntityCatalogEntry>(individualKey)
      if (individualData && optimisticUpdate) {
        queryClient.setQueryData(individualKey, updatedCatalogEntry)
      }

      // return rollback value
      return { list: previousData, entity: individualData }
    },
    onError: (err, catalogEntry, context) => {
      if (context) {
        const key = libraryCatalogEntriesQueryKeys.organization({ orgId })
        queryClient.setQueryData(key, context.list)
        const individualKey = libraryCatalogEntriesQueryKeys.entry({
          orgId,
          catalogEntryId: catalogEntry.id,
        })
        queryClient.setQueryData(individualKey, context.entity)
      }

      handleError(err)
    },
    onSuccess: (_, variables) => {
      if (updateOnSuccess) {
        const updatedData = {
          ...variables,
          last_modified: new Date().toISOString(),
        }

        // Update individual entry cache
        const individualKey = libraryCatalogEntriesQueryKeys.entry({
          orgId,
          catalogEntryId: variables.id,
        })
        queryClient.setQueryData(individualKey, updatedData)

        // Update all schema query caches that match this schema prefix
        // This handles all variations of the key (with different limit, offset, sort, etc.)
        if (variables.extended_schema_id) {
          const schemaKeyPrefix = libraryCatalogEntriesQueryKeys.schemaPrefix({
            orgId,
            schemaId: variables.extended_schema_id,
          })
          queryClient.setQueriesData<PaginatedResponse<EntityCatalogEntry>>(
            { queryKey: schemaKeyPrefix, exact: false },
            (old) => {
              if (!old) return old
              return {
                ...old,
                data: old.data.map((c) =>
                  c.id === variables.id ? updatedData : c,
                ),
              }
            },
          )
        }
      }

      if (invalidateOnSuccess) {
        const individualKey = libraryCatalogEntriesQueryKeys.entry({
          orgId,
          catalogEntryId: variables.id,
        })
        return Promise.all([
          queryClient.invalidateQueries({ queryKey: individualKey }),
        ])
      }
    },
  })
}
